/*
  Shift In Style: small page helpers, no libraries.
  1. Reveal on scroll: elements with .sis-reveal get .is-in when they enter the viewport.
     Re-scans after theme editor section loads, so new sections reveal too.
  2. Parallax, via one rAF scroll handler, off for reduced motion: full-width bands
     ([data-sis-parallax]) drift ±7% as they pass; banners at the top of the page
     ([data-sis-parallax="top"]: hero, collection banners) move at 30% of the scroll speed.
  3. Sideways rows (chips, tiles, review cards): a fade on the edge that has more to
     scroll, the active chip scrolled into view, and position dots for rows marked
     [data-sis-dots].
  4. Card photos on touch screens: light the mark for the image you swiped to.
  5. Collection chips and sort: load the new results in place (fetch the page, swap every
     section under the hero) instead of a full page load, so the page doesn't flash or
     jump to the top. Falls back to a normal page load if anything doesn't match.
*/
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px' }
      )
    : null;

function scanReveals(root = document) {
  for (const el of root.querySelectorAll('.sis-reveal:not(.is-in)')) {
    if (revealObserver && !reduceMotion.matches) revealObserver.observe(el);
    else el.classList.add('is-in');
  }
}

let parallaxImages = [];
let ticking = false;

function updateParallax() {
  ticking = false;
  const vh = window.innerHeight;
  for (const img of parallaxImages) {
    const box = img.parentElement.getBoundingClientRect();
    if (box.bottom < 0 || box.top > vh) continue;
    if (img.dataset.sisParallax === 'top') {
      // Banners at the top of the page: the photo moves at 30% of the scroll speed, so it
      // trails behind the page. The photo is 15% taller than its frame, so no gap shows.
      const scrolled = Math.min(Math.max(-box.top + img.parentElement.dataset.sisTop * 1, 0), box.height);
      img.style.transform = `translate3d(0, ${(scrolled * 0.3).toFixed(1)}px, 0)`;
      continue;
    }
    // -1 when the band enters at the bottom, +1 when it leaves at the top
    const progress = (vh - box.top) / (vh + box.height) * 2 - 1;
    img.style.transform = `translate3d(0, ${(-progress * 7).toFixed(2)}%, 0)`;
  }
}

function scanParallax() {
  parallaxImages = reduceMotion.matches ? [] : [...document.querySelectorAll('[data-sis-parallax]')];
  // Where each top banner sits with the page at rest (below the announcement bar)
  for (const img of parallaxImages) {
    const frame = img.parentElement;
    if (img.dataset.sisParallax === 'top') frame.dataset.sisTop = String(frame.getBoundingClientRect().top + window.scrollY);
  }
  updateParallax();
}

window.addEventListener(
  'scroll',
  () => {
    if (!parallaxImages.length || ticking) return;
    ticking = true;
    requestAnimationFrame(updateParallax);
  },
  { passive: true }
);

const SCROLLERS = '.sis-coll-hero__tabs, .sis-toolbar__chips, .sis-style-row__chips, .sis-cars__row, .sis-rail-reviews__rail';

function setupScroller(row) {
  if (row.dataset.sisScroller) return;
  row.dataset.sisScroller = 'on';

  // Bring the selected chip into view without moving the page
  const active = row.querySelector('[aria-current], [aria-pressed="true"]:not(:first-child)');
  if (active) row.scrollLeft = active.offsetLeft - (row.clientWidth - active.offsetWidth) / 2;

  let dots = null;
  if ('sisDots' in row.dataset && row.children.length > 1) {
    dots = document.createElement('div');
    dots.className = 'sis-dots';
    dots.setAttribute('aria-hidden', 'true');
    dots.innerHTML = [...row.children].map(() => '<i></i>').join('');
    row.after(dots);
  }

  const update = () => {
    const max = row.scrollWidth - row.clientWidth;
    row.classList.toggle('sis-scroll--left', row.scrollLeft > 4);
    row.classList.toggle('sis-scroll--right', row.scrollLeft < max - 4);
    if (dots) {
      dots.hidden = max <= 4;
      const step = row.children[1] ? row.children[1].offsetLeft - row.children[0].offsetLeft : row.clientWidth;
      const index = row.scrollLeft >= max - 4 ? row.children.length - 1 : Math.round(row.scrollLeft / step);
      [...dots.children].forEach((dot, i) => dot.classList.toggle('is-on', i === index));
    }
  };
  row.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
  new ResizeObserver(update).observe(row);
  update();
}

function scanScrollers(root = document) {
  root.querySelectorAll(SCROLLERS).forEach(setupScroller);
}

// Scroll events don't bubble, so listen in the capture phase for every card track at once
document.addEventListener(
  'scroll',
  (event) => {
    const track = event.target;
    if (!(track instanceof HTMLElement) || !track.classList.contains('sis-card-media__track')) return;
    const marks = track.nextElementSibling;
    if (!marks || !marks.classList.contains('sis-card-media__marks')) return;
    const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
    [...marks.children].forEach((mark, i) => mark.classList.toggle('is-on', i === index));
    marks.classList.toggle('is-alt', index > 0);
  },
  { capture: true, passive: true }
);

const FILTER_LINKS = '.sis-toolbar a.sis-chip, .sis-style-row a.sis-chip';
const HERO = '.sis-coll-hero, .sis-release';
const pending = new Map();

// Start loading as soon as a finger touches a chip or the mouse rests on one; the click
// then reuses the same request
function preload(url) {
  if (!pending.has(url)) {
    pending.set(url, fetch(url, { credentials: 'same-origin' }).then((r) => (r.ok ? r.text() : Promise.reject(r.status))));
    setTimeout(() => pending.delete(url), 30000);
  }
  return pending.get(url);
}

async function swapResults(url, push) {
  const main = document.getElementById('MainContent');
  if (!main) return location.assign(url);
  // Dim the products only if the wait is long enough to notice
  const dim = setTimeout(() => main.classList.add('sis-loading'), 150);
  try {
    const next = new DOMParser().parseFromString(await preload(url), 'text/html');
    const nextMain = next.getElementById('MainContent');
    const current = [...main.querySelectorAll(':scope > .shopify-section')];
    const incoming = nextMain ? [...nextMain.querySelectorAll(':scope > .shopify-section')] : [];
    if (!incoming.length || incoming.length !== current.length || incoming.some((s, i) => s.id !== current[i].id)) {
      throw new Error('layout changed');
    }
    // New results appear at once (Horizon would fade and slide each card in)
    main.classList.add('sis-instant');
    current.forEach((section, i) => {
      if (section.querySelector(HERO)) return;
      section.replaceWith(document.importNode(incoming[i], true));
    });
    main.querySelectorAll('.sis-reveal').forEach((el) => el.classList.add('is-in'));
    document.title = next.title;
    if (push) history.pushState({ sis: true }, '', url);
    scanScrollers(main);
    scanParallax();
    // If you were already down in the products, start the new results under the toolbar
    const toolbar = main.querySelector('.sis-toolbar');
    if (toolbar) {
      const header = parseFloat(getComputedStyle(toolbar).top) || 0;
      const top = toolbar.parentElement.getBoundingClientRect().top + window.scrollY - header;
      if (window.scrollY > top) window.scrollTo({ top, behavior: 'instant' });
    }
  } catch (error) {
    location.assign(url);
  } finally {
    clearTimeout(dim);
    main.classList.remove('sis-loading');
  }
}

for (const type of ['pointerdown', 'pointerover']) {
  document.addEventListener(
    type,
    (event) => {
      const link = event.target instanceof Element ? event.target.closest(FILTER_LINKS) : null;
      if (link && (type === 'pointerdown' || event.pointerType === 'mouse')) preload(link.href);
    },
    { passive: true }
  );
}

document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest(FILTER_LINKS) : null;
  if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
  event.preventDefault();
  swapResults(link.href, true);
});

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('sis-toolbar__sort')) return;
  event.preventDefault();
  const url = new URL(form.action, location.href);
  url.search = new URLSearchParams(new FormData(form)).toString();
  swapResults(url.toString(), true);
});

window.addEventListener('popstate', (event) => {
  if (event.state && event.state.sis) swapResults(location.href, false);
});

if (document.querySelector('.sis-toolbar')) history.replaceState({ sis: true }, '', location.href);

scanReveals();
scanParallax();
scanScrollers();

document.addEventListener('shopify:section:load', (event) => {
  scanReveals(event.target);
  scanParallax();
  scanScrollers(event.target);
});
